import React, { useState } from 'react';
import { z } from 'zod';

import { InterstitialFlowProps, UserRegistrationFormData } from '../../types';

import { UserRegistrationForm } from './UserRegistrationForm';
import { WelcomeScreen } from './WelcomeScreen';

// Zod schema for runtime validation of user registration data
export const userRegistrationFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  specialty: z.string().min(1, 'Please select your specialty'),
  ehrPreference: z.string().min(1, 'Please select your EHR'),
  organizationSize: z.string().min(1, 'Please select organization size'),
});

export type UserRegistrationData = z.infer<typeof userRegistrationFormSchema>;

/**
 * Safely parse and validate user registration data from unknown input
 */
export function parseUserRegistrationData(data: unknown): UserRegistrationData {
  const parseResult = userRegistrationFormSchema.safeParse(data);

  if (!parseResult.success) {
    throw new Error(
      `Invalid user registration data: ${parseResult.error.message}`,
    );
  }

  return parseResult.data;
}

/**
 * Extract user profile fields for the users table
 */
export function getUserProfileData(data: UserRegistrationData) {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    specialty: data.specialty,
  };
}

export const UserRegistrationFlow: React.FC<
  InterstitialFlowProps & {
    onComplete: (data: UserRegistrationFormData) => Promise<void>;
  }
> = ({ onComplete }) => {
  const [step, setStep] = useState<'form' | 'welcome'>('form');
  const [formData, setFormData] = useState<UserRegistrationFormData | null>(
    null,
  );

  const handleFormSubmit = (data: UserRegistrationFormData) => {
    setFormData(data);
    setStep('welcome');
  };

  const handleWelcomeContinue = async () => {
    if (formData) {
      await onComplete(formData);
    }
  };

  if (step === 'form') {
    return <UserRegistrationForm onSubmit={handleFormSubmit} />;
  }

  return <WelcomeScreen onContinue={handleWelcomeContinue} />;
};
